import { z } from 'zod';
import {
  modifiableProperties,
  PRIMARY_KEY,
  type LayerModification,
  type Modifications,
} from './modifications';
import type { LayerType } from './template';
import { MAX_SCALE, RENDER_FORMATS, sanitizeFilename } from './render';

export type DataRow = Record<string, string>;

export const fieldRuleSchema = z.object({
  /** Target layer name (applies to every selected template that has this layer). */
  layer: z.string().min(1).max(64),
  /** Layer property, or "$value" for the primary property (text, image, …). */
  property: z.string().min(1).max(64).default(PRIMARY_KEY),
  source: z.enum(['column', 'static']).default('column'),
  column: z.string().max(200).optional(),
  value: z.string().max(20000).optional(),
});
export type FieldRule = z.infer<typeof fieldRuleSchema>;

export const mappingSchema = z.object({
  /** Map columns named like layers ("title", "title.color") automatically. */
  auto: z.boolean().default(true),
  rules: z.array(fieldRuleSchema).max(500).default([]),
});
export type Mapping = z.infer<typeof mappingSchema>;

const rowsSchema = z.array(z.record(z.string(), z.string().max(20000))).max(100000);

export const dataSourceSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('table'),
    columns: z.array(z.string().max(200)).max(200),
    rows: rowsSchema,
    filename: z.string().max(255).optional(),
  }),
  z.object({
    type: z.literal('google_sheets'),
    url: z.url(),
    /** Optional connected Google account; without it the sheet must be shared "anyone with the link". */
    integrationId: z.string().nullable().default(null),
    sheet: z.string().max(200).optional(),
  }),
  z.object({
    type: z.literal('url'),
    url: z.url(),
    /** xml: product feeds (Google Merchant, Meta catalog, YML for Rozetka / Prom) or any XML. */
    format: z.enum(['csv', 'json', 'xml']).default('csv'),
  }),
  z.object({
    /** Rows are pushed through the API when the automation is triggered. */
    type: z.literal('api'),
  }),
]);
export type DataSource = z.infer<typeof dataSourceSchema>;

/** What the folders of an automation's files can be named after. */
export const OUTPUT_FOLDER_LEVELS = ['template', 'size', 'format', 'column'] as const;
export type OutputFolderLevel = (typeof OUTPUT_FOLDER_LEVELS)[number];

/**
 * Folder path of one file ("Sale/1080x1920"), each level a safe folder name; a level without a
 * value (an empty cell) is left out.
 */
export function outputFolder(
  levels: readonly OutputFolderLevel[],
  ctx: {
    template: string;
    width: number;
    height: number;
    format: string;
    row?: Record<string, unknown>;
    column?: string;
  },
): string {
  return levels
    .map((level) => {
      if (level === 'template') return ctx.template;
      if (level === 'size') return `${ctx.width}x${ctx.height}`;
      if (level === 'format') return ctx.format.toUpperCase();
      const v = ctx.column ? ctx.row?.[ctx.column] : undefined;
      return v === undefined || v === null ? '' : String(v);
    })
    .map((name) => sanitizeFilename(name).slice(0, 100))
    .filter(Boolean)
    .join('/');
}

export const outputSchema = z.object({
  formats: z.array(z.enum(RENDER_FORMATS)).min(1).max(4).default(['png']),
  scale: z.number().min(0.1).max(MAX_SCALE).default(1),
  quality: z.number().int().min(1).max(100).default(90),
  filenamePattern: z.string().max(200).default('{{template}}-{{index}}'),
  /**
   * Render only rows that are new or changed since the previous run (a product feed checked
   * every day). A change of the templates, mapping or output settings renders everything again.
   */
  onlyChanged: z.boolean().default(false),
  /** Column that identifies a row from run to run (a product id); without it, the whole row. */
  keyColumn: z.string().max(200).default('id'),
  /**
   * Folders of the files in the ZIP, on Google Drive and S3, outer first: a folder per template,
   * per size, per format or per value of a column (a category). Empty: all files together.
   */
  folders: z.array(z.enum(OUTPUT_FOLDER_LEVELS)).max(4).default([]),
  /** Column whose value names the folder of the `column` level. */
  folderColumn: z.string().max(200).default(''),
});
export type OutputSettings = z.infer<typeof outputSchema>;

export const destinationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('zip') }),
  z.object({
    type: z.literal('google_drive'),
    integrationId: z.string(),
    folderName: z.string().min(1).max(200).default('Bannermatic'),
    folderId: z.string().optional(),
    subfolderPerRun: z.boolean().default(true),
  }),
  z.object({ type: z.literal('webhook'), url: z.url() }),
  z.object({
    type: z.literal('s3'),
    integrationId: z.string(),
    prefix: z.string().max(500).default('bannermatic/'),
  }),
]);
export type Destination = z.infer<typeof destinationSchema>;

export const scheduleSchema = z
  .object({
    cron: z.string().min(9).max(120),
    timezone: z.string().min(1).max(64).default('UTC'),
  })
  .nullable();
export type Schedule = z.infer<typeof scheduleSchema>;

export const automationInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  templateIds: z.array(z.string()).min(1).max(100),
  dataSource: dataSourceSchema,
  mapping: mappingSchema.default({ auto: true, rules: [] }),
  output: outputSchema.default({
    formats: ['png'],
    scale: 1,
    quality: 90,
    filenamePattern: '{{template}}-{{index}}',
    onlyChanged: false,
    keyColumn: 'id',
    folders: [],
    folderColumn: '',
  }),
  destinations: z
    .array(destinationSchema)
    .max(10)
    .default([{ type: 'zip' }]),
  schedule: scheduleSchema.default(null),
  enabled: z.boolean().default(true),
  /** Brand kit for every render of the run (null: each template's own brand). */
  brandId: z.string().max(64).nullable().default(null),
});
export type AutomationInput = z.infer<typeof automationInputSchema>;

function isEmptyRow(row: DataRow): boolean {
  return Object.values(row).every((v) => v === undefined || v === null || String(v).trim() === '');
}

export function cleanRows(rows: DataRow[]): DataRow[] {
  return rows.filter((r) => !isEmptyRow(r));
}

/**
 * Turn one data row into modifications for a template with the given layer names.
 * Auto-mapping: column "title" → layer "title" primary value, "title.color" → its color.
 * Explicit rules run after auto-mapping and override it.
 */
/**
 * Layers an automation may fill: a layer locked in the editor stays as designed, whatever the
 * table holds for it.
 */
export function automatableLayerNames(doc: { layers: Array<{ name: string; locked?: boolean }> }) {
  return doc.layers.filter((l) => !l.locked).map((l) => l.name);
}

export function rowToModifications(
  row: DataRow,
  mapping: Mapping,
  layerNames: Iterable<string>,
): Modifications {
  const names = new Set(layerNames);
  const mods: Record<string, LayerModification> = {};
  // Empty cells keep the template's value; use a "<layer>.hide" column to hide a layer.
  const set = (layer: string, prop: string, value: string) => {
    if (!names.has(layer) || value.trim() === '') return;
    (mods[layer] ??= {})[prop] = value;
  };

  if (mapping.auto) {
    for (const [column, value] of Object.entries(row)) {
      const key = column.trim();
      if (value === undefined || value === null) continue;
      const dot = key.indexOf('.');
      if (dot === -1) {
        if (names.has(key)) set(key, PRIMARY_KEY, String(value));
      } else {
        set(key.slice(0, dot), key.slice(dot + 1), String(value));
      }
    }
  }

  for (const rule of mapping.rules) {
    const value =
      rule.source === 'static' ? (rule.value ?? '') : rule.column ? row[rule.column] : undefined;
    if (value === undefined) continue;
    set(rule.layer, rule.property || PRIMARY_KEY, String(value));
  }

  return mods;
}

/** Collect column names across rows preserving first-seen order. */
export function columnsOf(rows: DataRow[]): string[] {
  const seen = new Set<string>();
  for (const r of rows) for (const k of Object.keys(r)) seen.add(k);
  return [...seen];
}

// ---------------------------------------------------------------- suggested mapping

/** Header words people use for the same field (any language of the interface). */
const SYNONYMS: string[][] = [
  [
    'title',
    'headline',
    'heading',
    'header',
    'name',
    'заголовок',
    'назва',
    'название',
    'titulo',
    'tytul',
    'titlu',
  ],
  [
    'subtitle',
    'subheading',
    'subheadline',
    'tagline',
    'подзаголовок',
    'підзаголовок',
    'subtitulo',
    'podtytul',
  ],
  [
    'description',
    'desc',
    'body',
    'text',
    'описание',
    'опис',
    'текст',
    'descripcion',
    'opis',
    'descriere',
  ],
  ['price', 'cost', 'цена', 'ціна', 'стоимость', 'вартість', 'precio', 'cena', 'pret'],
  ['oldprice', 'priceold', 'regularprice', 'старая цена', 'стара ціна', 'precioanterior'],
  ['discount', 'sale', 'скидка', 'знижка', 'descuento', 'rabat', 'reducere'],
  [
    'image',
    'photo',
    'picture',
    'img',
    'фото',
    'картинка',
    'изображение',
    'зображення',
    'imagen',
    'foto',
    'zdjecie',
    'imagine',
  ],
  ['logo', 'лого', 'логотип', 'emblem', 'эмблема', 'емблема'],
  ['button', 'cta', 'кнопка', 'boton', 'przycisk', 'buton'],
  ['date', 'дата', 'fecha', 'data'],
  ['time', 'время', 'час', 'hora', 'czas', 'ora'],
  ['url', 'link', 'qr', 'ссылка', 'посилання', 'enlace', 'odnosnik'],
  ['background', 'bg', 'фон', 'fondo', 'tlo', 'fundal'],
  ['color', 'colour', 'цвет', 'колір'],
];

/** Lowercase letters and digits only: "Home Team" / "home_team" / "homeTeam" → "hometeam". */
export function normalizeHeader(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

function tokens(s: string): string[] {
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

const synonymIndex = new Map<string, number>();
SYNONYMS.forEach((group, i) => group.forEach((w) => synonymIndex.set(normalizeHeader(w), i)));

/** How well a header names a layer: 1 identical, 0.9 synonyms, ~0.6–0.8 partial, 0 no. */
export function headerScore(header: string, layer: string): number {
  const a = normalizeHeader(header);
  const b = normalizeHeader(layer);
  if (!a || !b) return 0;
  if (a === b) return 1;
  const sa = synonymIndex.get(a);
  if (sa !== undefined && sa === synonymIndex.get(b)) return 0.9;
  // Same words with a number: "Team 1" ↔ "team1" is caught above; "logo_home" ↔ "Home logo"
  const ta = tokens(header);
  const tb = tokens(layer);
  const setB = new Set(tb.map((w) => synonymIndex.get(w) ?? w));
  const mapped = ta.map((w) => synonymIndex.get(w) ?? w);
  if (ta.length && tb.length && mapped.length === setB.size && mapped.every((w) => setB.has(w)))
    return 0.85;
  // One name contains the other ("Product title" ↔ "title"), but not 2-letter fragments
  const [short, long] = a.length < b.length ? [a, b] : [b, a];
  if (short.length >= 3 && long.includes(short)) return 0.6 + (short.length / long.length) * 0.2;
  return 0;
}

const looksLikeImage = (v: string | undefined) =>
  !!v &&
  (/^https?:\/\/\S+$/i.test(v.trim()) || /^media:/i.test(v.trim()) || v.startsWith('/files/'));

export interface MappingLayer {
  name: string;
  /** `audio`: the music or voice-over of a video (`@music`, `@voice`). */
  type: LayerType | 'audio';
}

/**
 * Rules connecting table columns to template layers by their headers (and, for images, by the
 * first row's values), so a freshly loaded sheet works without manual mapping. Layers that
 * already have a rule, and columns the exact auto-mapping handles, are left alone.
 */
export function suggestMapping(
  columns: string[],
  layers: MappingLayer[],
  mapping: Mapping,
  firstRow?: DataRow,
): FieldRule[] {
  const cols = columns.filter((c) => c.trim());
  const names = new Set(layers.map((l) => l.name));
  const taken = new Set(mapping.rules.map((r) => `${r.layer}\u0000${r.property || PRIMARY_KEY}`));
  const usedColumns = new Set(
    mapping.rules.filter((r) => r.source === 'column').map((r) => r.column),
  );
  const out: FieldRule[] = [];
  const add = (layer: string, property: string, column: string) => {
    const key = `${layer}\u0000${property}`;
    if (taken.has(key) || usedColumns.has(column)) return false;
    taken.add(key);
    usedColumns.add(column);
    out.push({ layer, property, source: 'column', column });
    return true;
  };

  // Exact auto-mapping already covers "title" and "title.color" when it is on
  if (mapping.auto) {
    for (const c of cols) {
      const key = c.trim();
      const dot = key.indexOf('.');
      const layer = dot === -1 ? key : key.slice(0, dot);
      if (names.has(layer)) {
        usedColumns.add(c);
        taken.add(`${layer}\u0000${dot === -1 ? PRIMARY_KEY : key.slice(dot + 1)}`);
      }
    }
  }

  // "Title color" / "title.colour" / "Team 1 logo.Hide": a layer followed by a property
  for (const c of cols) {
    if (usedColumns.has(c)) continue;
    const parts = c.includes('.')
      ? [c.slice(0, c.lastIndexOf('.')), c.slice(c.lastIndexOf('.') + 1)]
      : null;
    const words = tokens(c);
    const splits: Array<[string, string]> = parts
      ? [[parts[0], parts[1]]]
      : words.length > 1
        ? [[words.slice(0, -1).join(' '), words.at(-1)!]]
        : [];
    for (const [layerPart, propPart] of splits) {
      let best: { layer: MappingLayer; score: number } | null = null;
      for (const l of layers) {
        const score = headerScore(layerPart, l.name);
        if (score >= 0.85 && (!best || score > best.score)) best = { layer: l, score };
      }
      if (!best) continue;
      const wanted = normalizeHeader(propPart);
      const prop = modifiableProperties(best.layer.type).find(
        (p) =>
          normalizeHeader(p) === wanted ||
          (synonymIndex.get(wanted) !== undefined &&
            synonymIndex.get(wanted) === synonymIndex.get(normalizeHeader(p))),
      );
      if (prop) add(best.layer.name, prop, c);
    }
  }

  // Primary values: best header match first
  const pairs: Array<{ column: string; layer: string; score: number }> = [];
  for (const c of cols) {
    if (usedColumns.has(c)) continue;
    for (const l of layers) {
      if (taken.has(`${l.name}\u0000${PRIMARY_KEY}`)) continue;
      const score = headerScore(c, l.name);
      if (score >= 0.6) pairs.push({ column: c, layer: l.name, score });
    }
  }
  pairs.sort((x, y) => y.score - x.score);
  for (const p of pairs) add(p.layer, PRIMARY_KEY, p.column);

  // The first row decides for the one image left: a single column of links goes to it
  if (firstRow) {
    const images = layers.filter(
      (l) => l.type === 'image' && !taken.has(`${l.name}\u0000${PRIMARY_KEY}`),
    );
    const linkColumns = cols.filter((c) => !usedColumns.has(c) && looksLikeImage(firstRow[c]));
    if (images.length === 1 && linkColumns.length === 1)
      add(images[0].name, PRIMARY_KEY, linkColumns[0]);
  }
  return out;
}
